import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function ResourceManagement() {
  const authCtx = useContext(AuthContext);
  const optionalLocationText = value => {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    return trimmed === '' ? null : trimmed;
  };

  if (!authCtx.checkRight('resource:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const resourceSchema = {
    name: {
      label: 'Name',
      api: { itemField: 'name' },
      type: 'text',
      required: true,
      minWidth: '200px',
    },
    shortText: {
      label: 'Kurztext',
      api: { itemField: 'shortText' },
      type: 'text',
      required: false,
      minWidth: '220px',
    },
    description: {
      label: 'Beschreibung',
      api: { itemField: 'description' },
      type: 'text',
      required: false,
      minWidth: '360px',
      previewInList: true,
      previewMaxLines: 3,
      useHyphens: true,
    },
    htmlDescription: {
      label: 'HTML Beschreibung',
      api: { itemField: 'htmlDescription' },
      type: 'textarea',
      required: false,
      minWidth: '360px',
      previewInList: true,
      previewMaxLines: 3,
      listDisplayValue: value => (value ? value.replace(/<[^>]*>/g, '') : ''),
    },
    bookingConfirmationLocationOverride: {
      label: 'Ort in Buchungsbestätigung',
      api: { itemField: 'bookingConfirmationLocationOverride' },
      type: 'object',
      required: false,
      excludeFromAdd: true,
      excludeFromList: true,
      displayValue: value => {
        if (!value || typeof value !== 'object') return 'Standard vom Entertainment';
        const parts = [value.legalName, value.street, value.postcode, value.city]
          .map(optionalLocationText)
          .filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'Standard vom Entertainment';
      },
      schema: {
        legalName: {
          label: 'Name/Firma',
          type: 'text',
          required: false,
          placeholder: 'Leer = Entertainment/Rechtsträger',
          transformValue: optionalLocationText,
        },
        street: {
          label: 'Straße',
          type: 'text',
          required: false,
          placeholder: 'Leer = Entertainment/Rechtsträger',
          transformValue: optionalLocationText,
        },
        postcode: {
          label: 'PLZ',
          type: 'text',
          required: false,
          placeholder: 'Leer = Entertainment/Rechtsträger',
          transformValue: optionalLocationText,
        },
        city: {
          label: 'Ort',
          type: 'text',
          required: false,
          placeholder: 'Leer = Entertainment/Rechtsträger',
          transformValue: optionalLocationText,
        },
      },
    },
    image: {
      label: 'Bild-URL',
      api: { itemField: 'image' },
      type: 'text',
      required: false,
      minWidth: '240px',
    },
    type: {
      label: 'Typ',
      api: { itemField: 'type' },
      type: 'select',
      options: [
        { value: 'karaoke', label: 'Karaoke' },
        { value: 'escape-room', label: 'Escape Room' },
        { value: 'golf', label: 'Golf' },
        { value: 'bash', label: 'Bash' },
      ],
      required: true,
      minWidth: '160px',
    },
    productIds: {
      label: 'Produkte',
      type: 'multi-select',
      api: {
        resource: 'product/minimal-info',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Produkte auswählen',
      required: false,
      minWidth: '260px',
    },
    minPersons: {
      label: 'Min. Personen',
      api: { itemField: 'minPersons' },
      type: 'number',
      required: true,
      minWidth: '140px',
    },
    maxPersons: {
      label: 'Max. Personen',
      api: { itemField: 'maxPersons' },
      type: 'number',
      required: true,
      minWidth: '140px',
    },
    employeeWorkload: {
      label: 'Mitarbeiter-Workload (%)',
      api: { itemField: 'employeeWorkload' },
      type: 'number',
      required: false,
      minWidth: '220px',
    },
    startOffsetMinutes: {
      label: 'Start Offset (Minuten)',
      api: { itemField: 'startOffsetMinutes' },
      type: 'number',
      required: false,
      minWidth: '180px',
    },
    active: {
      label: 'Aktiv',
      api: { itemField: 'active' },
      type: 'boolean',
      required: false,
      minWidth: '120px',
    },
    sortOrder: {
      label: 'Sortierung',
      api: { itemField: 'sortOrder' },
      type: 'number',
      required: false,
      minWidth: '120px',
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={resourceSchema}
      title='Neue Ressource anlegen'
      resourceType='resource'
      headerSearchPlaceholder='Ressourcen durchsuchen'
      parentIsEditable={authCtx.checkRight('resource:update')}
      options={{ titleKey: 'name' }}
    />
    </Box>
  );
}

export default ResourceManagement;
